import { OVERALL_HEALTH_DOMAIN, WELLNESS_DOMAINS_LIST } from '@/constants';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import roles from '@/constants/roles';
import { useGrowthZone } from '@/hooks/growthzone/useGrowthZone';
import SmartFrameworkWithTooltip from '@/modules/growth-zone/SmartFrameworkWithTooltip';
import RetakeScreenerReminderWithButton from '@/modules/screener/RetakeScreenerReminderWithButton';
import GrowthZoneDomainItem from '@/modules/user/growthZone/GrowthZoneDomainItem';
import { useAppStore } from '@/store/useAppStore';
import { text } from '@/theme/colors/palettes';
import { EGrowthDomainGoalTrend } from '@/types/GrowthZone.types';
import { THealthDomainWithOverall } from '@/types/Health.types';
import { Box, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';

const GROWTH_ZONE_DOMAINS: THealthDomainWithOverall[] = [...WELLNESS_DOMAINS_LIST, OVERALL_HEALTH_DOMAIN];

const UserGoalsPage = () => {
  const { data, isLoading, refetch, isRefetching } = useGrowthZone();
  const { user } = useAppStore();

  return (
    <VStack width={MAX_CONTAINER_WIDTH} paddingBottom={'20px'}>
      {/* Actual page content */}
      <VStack width={'100%'} marginBottom={'20px'} gap={'10px'}>
        <Text variant={'loraMedium22'} color={text.darkBlue}>
          Growth Zone
        </Text>
        <VStack gap={0}>
          <Text variant={'urbanistMedium'} color={text.mediumGray}>
            With focused effort, and support, growth is possible!
          </Text>
          <SmartFrameworkWithTooltip fontWeight={500} />
        </VStack>
      </VStack>

      <HStack width={'100%'} wrap={'wrap'} gap={'20px'} justifyContent={'center'}>
        {GROWTH_ZONE_DOMAINS.map((domain) => (
          <VStack width={'32%'} key={domain}>
            {isLoading ? (
              <Box width={'100%'} borderRadius="14px" p="20px" bg="white" boxShadow="0px 10px 40px 0px #00417926">
                <VStack align="start" w="full" spacing="20px" flex={1}>
                  <HStack justifyContent={'space-between'} w={'full'} alignItems={'flex-end'}>
                    <Skeleton height="18px" width="100px" borderRadius={'4px'} />
                    <Skeleton
                      height="12px"
                      width="100px"
                      borderRadius={'4px'}
                      startColor="gray.200"
                      endColor="gray.100"
                    />
                  </HStack>
                  <HStack align="end" spacing="14px">
                    <VStack spacing={'14px'}>
                      <Skeleton width="355px" height="50px" borderRadius={'10px'} />
                      <Skeleton width="355px" height="50px" borderRadius={'10px'} />
                      <Skeleton width="355px" height="50px" borderRadius={'10px'} />
                    </VStack>
                  </HStack>
                </VStack>
              </Box>
            ) : (
              <GrowthZoneDomainItem
                domainGoalData={{
                  createdAt: '',
                  domain,
                  goals: data?.find((goals) => goals.domain === domain)?.goals ?? [],
                  score: data?.find((goals) => goals.domain === domain)?.score ?? 1,
                  trend: data?.find((goals) => goals.domain === domain)?.trend ?? EGrowthDomainGoalTrend.IMPROVING,
                }}
                refetchAllGoals={refetch}
                isRefetchingAllGoals={isRefetching}
                showMultipleCreateItems
              />
            )}
          </VStack>
        ))}
      </HStack>

      {user?.roles.length === 1 && user.roles[0] === roles.contentCreator ? null : (
        <RetakeScreenerReminderWithButton
          containerStyle={{ width: '400px', marginTop: '80px', marginBottom: '80px' }}
        />
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_user/user/growth-zone')({
  staticData: { title: 'Growth Zone' },
  component: UserGoalsPage,
});
